import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DocumentSnapshot } from "firebase/firestore";
import dreamWorldProvider from "../DreamWorldProvider";
import { LibraryItem } from "../types/library";
import { RootState } from "./store";
import { UserData } from "./dreamworldSlice";

import { Effect } from "../types/effects";

interface DiscoverState {
  content: LibraryItem[];
  hasMore: boolean;
  loading: "idle" | "pending" | "succeeded" | "failed";
  lastVisible: DocumentSnapshot | null;
  trendingCreators: UserData[];
  trendingCreatorsLoading: "idle" | "pending" | "succeeded" | "failed";
  allCreators: UserData[];
  allCreatorsLoading: "idle" | "pending" | "succeeded" | "failed";
  recentEffects: Effect[];
  recentEffectsLoading: "idle" | "pending" | "succeeded" | "failed";
}

const initialState: DiscoverState = {
  content: [],
  hasMore: true,
  loading: "idle",
  lastVisible: null,
  trendingCreators: [],
  trendingCreatorsLoading: "idle",
  allCreators: [],
  allCreatorsLoading: "idle",
  recentEffects: [],
  recentEffectsLoading: "idle",
};

export const getDiscoverContent = createAsyncThunk(
  "discover/getDiscoverContent",
  async (lastVisible: DocumentSnapshot | undefined, { getState }) => {
    const { items, lastVisible: newLastVisible } =
      await dreamWorldProvider.getPublishedLibraryContent(lastVisible);
    return { items, newLastVisible };
  }
);

export const getTrendingCreators = createAsyncThunk(
  "discover/getTrendingCreators",
  async () => {
    const creators = await dreamWorldProvider.getTrendingCreators();
    return creators;
  }
);

export const getAllCreators = createAsyncThunk(
  "discover/getAllCreators",
  async () => {
    const creators = await dreamWorldProvider.getAllCreators();
    return creators;
  }
);

export const getRecentEffects = createAsyncThunk(
  "discover/getRecentEffects",
  async (userId: string, { getState }) => {
    const { effects } = await dreamWorldProvider.getRecentEffects(userId);
    return effects;
  }
);

export const discoverSlice = createSlice({
  name: "discover",
  initialState,
  reducers: {
    setDiscoverContent: (state, action: PayloadAction<LibraryItem[]>) => {
      state.content = action.payload;
    },
    updateDiscoverContent: (state, action: PayloadAction<LibraryItem>) => {
      const index = state.content.findIndex(
        (item) => item.id === action.payload.id
      );
      if (index !== -1) {
        state.content[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDiscoverContent.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(getDiscoverContent.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.content = [...state.content, ...action.payload.items];
        state.lastVisible = action.payload.newLastVisible;
        state.hasMore = !!action.payload.newLastVisible;
      })
      .addCase(getDiscoverContent.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(getTrendingCreators.pending, (state) => {
        state.trendingCreatorsLoading = "pending";
      })
      .addCase(getTrendingCreators.fulfilled, (state, action) => {
        state.trendingCreatorsLoading = "succeeded";
        state.trendingCreators = action.payload;
      })
      .addCase(getTrendingCreators.rejected, (state) => {
        state.trendingCreatorsLoading = "failed";
      })
      .addCase(getAllCreators.pending, (state) => {
        state.allCreatorsLoading = "pending";
      })
      .addCase(getAllCreators.fulfilled, (state, action) => {
        state.allCreatorsLoading = "succeeded";
        state.allCreators = action.payload;
      })
      .addCase(getAllCreators.rejected, (state) => {
        state.allCreatorsLoading = "failed";
      })
      .addCase(getRecentEffects.pending, (state) => {
        state.recentEffectsLoading = "pending";
      })
      .addCase(getRecentEffects.fulfilled, (state, action) => {
        state.recentEffectsLoading = "succeeded";
        state.recentEffects = action.payload;
      })
      .addCase(getRecentEffects.rejected, (state) => {
        state.recentEffectsLoading = "failed";
      });
  },
});

export const { setDiscoverContent, updateDiscoverContent } =
  discoverSlice.actions;

export const selectDiscoverContent = (state: RootState) =>
  state.discover.content;
export const selectDiscoverHasMore = (state: RootState) =>
  state.discover.hasMore;
export const selectDiscoverLoading = (state: RootState) =>
  state.discover.loading;
export const selectTrendingCreators = (state: RootState) =>
  state.discover.trendingCreators;
export const selectTrendingCreatorsLoading = (state: RootState) =>
  state.discover.trendingCreatorsLoading;
export const selectAllCreators = (state: RootState) =>
  state.discover.allCreators;
export const selectAllCreatorsLoading = (state: RootState) =>
  state.discover.allCreatorsLoading;
export const selectRecentEffects = (state: RootState) =>
  state.discover.recentEffects;
export const selectRecentEffectsLoading = (state: RootState) =>
  state.discover.recentEffectsLoading;

export default discoverSlice.reducer;
